import { useState, useMemo } from 'react';

import { FlexColumn } from 'approot/shared/styles/flex.style';
import { Heading18, PStyled } from 'approot/shared/styles/typography.style';
import {
  LinkButton,
  SecondaryButton,
  SecondaryButtonIcon,
} from 'approot/shared/styles/buttons/buttons';
import { isEverythingCorrect } from './interactive-response-shared.utils';
import {
  DragItem,
  DropItem,
} from '../../shared/interactive-drag-drop/interactive-drag-drop.constants';
import IconClose from 'approot/shared/icons/icon-close';
import { MEDIA_SM, useMediaQuery } from 'approot/shared/style';
import { useKeyBoardTrap } from 'approot/shared/ally/keyboard.hooks';
import { useAccessibleHeading } from 'approot/shared/ally/accessible-heading';
import * as Styled from './interactive-response-check-answer.style';
import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import {
  LargePillPrimaryButton,
  LargePillSecondaryButton,
} from 'approot/shared/styles/buttons/large-pill-button.style';
import { Box } from 'approot/shared/design-system/box/box.style';

const SeeResultsAndStartOver = ({
  dropItems,
  dragItems,
  onStartOver,
  onTryAgain,
  moreInformation,
  isCheckAnswerAtEnd,
}: {
  dropItems: DropItem[];
  dragItems: DragItem[];
  onStartOver: () => void;
  onTryAgain?: () => void;
  moreInformation: string | null;
  isCheckAnswerAtEnd?: boolean;
}) => {
  const { registerHeading } = useAccessibleHeading();

  const isCorrect = useMemo(
    () => isEverythingCorrect(dropItems, dragItems),
    [dropItems, dragItems]
  );

  return (
    <Styled.CheckAnswerWrapper>
      <ScreenReaderOnly tabIndex={-1} ref={registerHeading} as="h2">
        See Results then press start over to play again
      </ScreenReaderOnly>
      {isCorrect ? (
        <>
          <Heading18
            marginBottom={moreInformation ? 16 : 0}
            data-selector="interactive-result"
            tabIndex={-1}
            ref={registerHeading}
          >
            Good job! <span aria-hidden>🎉</span>
          </Heading18>
          {moreInformation && (
            <Styled.CheckAnswerMoreInfo>
              {moreInformation}
            </Styled.CheckAnswerMoreInfo>
          )}
        </>
      ) : (
        <>
          <Heading18
            marginBottom={16}
            data-selector="interactive-result"
            tabIndex={-1}
            ref={registerHeading}
          >
            Don’t give up!
          </Heading18>

          {isCheckAnswerAtEnd && (
            <LargePillSecondaryButton
              type="button"
              aria-label="Try again with incorrect items"
              data-selector="try-again-button"
              onClick={onTryAgain || onStartOver}
            >
              Try Again
            </LargePillSecondaryButton>
          )}
        </>
      )}
    </Styled.CheckAnswerWrapper>
  );
};

const CheckAnswer = ({ onCheckAnswer }: { onCheckAnswer: () => void }) => {
  const { registerHeading } = useAccessibleHeading();
  return (
    <FlexColumn>
      <ScreenReaderOnly tabIndex={-1} ref={registerHeading} as="h2">
        Everything has been answered. You can check your answers.
      </ScreenReaderOnly>
      <LargePillPrimaryButton
        type="button"
        data-selector="check-answer-button"
        onClick={onCheckAnswer}
      >
        Check Answer
      </LargePillPrimaryButton>
    </FlexColumn>
  );
};

export const InteractiveResponseCheckAnswer = ({
  isEverythingAnswered,
  isAnswerChecked,
  isCheckAnswerAtEnd,
  dropItems,
  dragItems,
  onStartOver,
  onTryAgain,
  onCheckAnswer,
  moreInformation,
}: {
  isEverythingAnswered: boolean;
  isAnswerChecked: boolean;
  isCheckAnswerAtEnd: boolean;
  dropItems: DropItem[];
  dragItems: DragItem[];
  onStartOver: () => void;
  onTryAgain?: () => void;
  onCheckAnswer?: () => void;
  moreInformation: string | null;
}) => {
  const isMediaUpSm = useMediaQuery(MEDIA_SM);

  return (
    <>
      {isEverythingAnswered && !isCheckAnswerAtEnd && (
        <Styled.InteractiveResponseCheck>
          <SeeResultsAndStartOver
            dropItems={dropItems}
            dragItems={dragItems}
            onStartOver={onStartOver}
            onTryAgain={onTryAgain}
            moreInformation={moreInformation}
            isCheckAnswerAtEnd={isCheckAnswerAtEnd}
          />
        </Styled.InteractiveResponseCheck>
      )}
      {isMediaUpSm && isEverythingAnswered && isCheckAnswerAtEnd && (
        <Styled.InteractiveResponseCheck>
          {isAnswerChecked ? (
            <SeeResultsAndStartOver
              dropItems={dropItems}
              dragItems={dragItems}
              onStartOver={onStartOver}
              onTryAgain={onTryAgain}
              moreInformation={moreInformation}
              isCheckAnswerAtEnd={isCheckAnswerAtEnd}
            />
          ) : onCheckAnswer ? (
            <CheckAnswer onCheckAnswer={onCheckAnswer} />
          ) : null}
        </Styled.InteractiveResponseCheck>
      )}
    </>
  );
};

export const CheckAnswerSmall = ({
  result,
  moreInformation,
  isActive,
  setIsActive,
  onStartOver,
  onTryAgain,
  isEverythingCorrect,
}: {
  result: string;
  moreInformation?: string | null;
  isActive: boolean;
  isEverythingCorrect?: boolean;
  setIsActive: (state: boolean) => void;
  onStartOver: () => void;
  onTryAgain?: () => void;
}) => {
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);

  useKeyBoardTrap({
    containerRef,
    isActive,
    captureKeypress: true,
    onOpenCloseDelay: 0,
    close: () => setIsActive(false),
  });

  return isActive ? (
    <Styled.CheckAnswer ref={setContainerRef} tabIndex={-1}>
      <Styled.CheckAnswerContainer>
        <SecondaryButtonIcon
          type="button"
          $borderRadius={40}
          $noBorder
          $isSmall
          aria-label="Close the check answer section"
          onClick={() => setIsActive(false)}
        >
          <IconClose width={14} height={14} />
        </SecondaryButtonIcon>
        <Styled.CheckAnswerTitle>{result}</Styled.CheckAnswerTitle>
        {moreInformation && isEverythingCorrect && (
          <Styled.CheckAnswerText>{moreInformation}</Styled.CheckAnswerText>
        )}
        <Box $alignItems="center" $justifyContent="center">
          <SecondaryButton
            type="button"
            $borderRadius={80}
            onClick={() => {
              setIsActive(false);
              if (isEverythingCorrect) {
                onStartOver();
              } else {
                (onTryAgain || onStartOver)();
              }
            }}
          >
            {isEverythingCorrect ? 'Start Over' : 'Try Again'}
          </SecondaryButton>
        </Box>
      </Styled.CheckAnswerContainer>
    </Styled.CheckAnswer>
  ) : null;
};

export const SubmitAnswerSmall = ({
  moreInformation,
  isActive,
  setIsActive,
  onSubmit,
}: {
  moreInformation?: string | null;
  isActive: boolean;
  setIsActive: (state: boolean) => void;
  onSubmit: () => void;
}) => {
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);

  useKeyBoardTrap({
    containerRef,
    isActive,
    captureKeypress: true,
    onOpenCloseDelay: 0,
    close: () => setIsActive(false),
  });

  return isActive ? (
    <Styled.CheckAnswer ref={setContainerRef} tabIndex={-1}>
      <Styled.CheckAnswerContainer>
        <Box $alignItems="center" $justifyContent="flex-end">
          <SecondaryButtonIcon
            type="button"
            $borderRadius={40}
            $noBorder
            $isSmall
            aria-label="Close the submit section"
            onClick={() => setIsActive(false)}
          >
            <IconClose width={14} height={14} />
          </SecondaryButtonIcon>
        </Box>
        <PStyled marginBottom={12} textAlignCenter>
          Ready to submit your answer?
        </PStyled>
        {moreInformation && (
          <Styled.CheckAnswerText>{moreInformation}</Styled.CheckAnswerText>
        )}
        <Box $alignItems="center" $justifyContent="center">
          <SecondaryButton
            type="button"
            $borderRadius={80}
            onClick={() => {
              onSubmit();
              setIsActive(false);
            }}
          >
            Submit
          </SecondaryButton>
        </Box>
      </Styled.CheckAnswerContainer>
    </Styled.CheckAnswer>
  ) : null;
};

export const ChangeAnswerSmall = ({
  moreInformation,
  isActive,
  setIsActive,
  onStartOver,
}: {
  moreInformation?: string | null;
  isActive: boolean;
  setIsActive: (state: boolean) => void;
  onStartOver: () => void;
}) => {
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);

  useKeyBoardTrap({
    containerRef,
    isActive,
    captureKeypress: true,
    onOpenCloseDelay: 0,
    close: () => setIsActive(false),
  });

  return isActive ? (
    <Styled.CheckAnswer ref={setContainerRef} tabIndex={-1}>
      <Styled.CheckAnswerContainer>
        <Box $alignItems="center" $justifyContent="flex-end">
          <SecondaryButtonIcon
            type="button"
            $borderRadius={40}
            $noBorder
            $isSmall
            aria-label="Close the check answer section"
            onClick={() => setIsActive(false)}
          >
            <IconClose width={14} height={14} />
          </SecondaryButtonIcon>
        </Box>
        <PStyled marginBottom={12} textAlignCenter>
          Your answer has been submitted.
        </PStyled>
        {moreInformation && (
          <Styled.CheckAnswerText>{moreInformation}</Styled.CheckAnswerText>
        )}
        <Box $alignItems="center" $justifyContent="center">
          <LinkButton
            type="button"
            onClick={() => {
              onStartOver();
              setIsActive(false);
            }}
          >
            Change Answer
          </LinkButton>
        </Box>
      </Styled.CheckAnswerContainer>
    </Styled.CheckAnswer>
  ) : null;
};
